import { useState } from 'react';
import { SmartConfirm } from '@/components/ui/SmartConfirm';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Plus,
  Pencil,
  Trash2,
  Thermometer,
  Gauge,
  Clock,
  Loader2,
  FlaskConical,
  ChevronDown,
  ChevronRight,
  Zap,
  TrendingUp,
} from 'lucide-react';
import { ModuleTabs } from '@/components/ui/ModuleTabs';
import { cn } from '@/lib/utils';

interface RampData {
  enabled: boolean;
  target_temp: number | null;
  pressure_bar: number | null;
  rate_deg_min: number | null;
  hold_minutes: number | null;
}

interface CuringRecipe {
  id: string;
  name: string;
  code: string | null;
  description: string | null;
  temperature_celsius: number;
  pressure_bar: number;
  duration_minutes: number;
  ramp_rate: number | null;
  cool_down_rate: number | null;
  material_type: string | null;
  resin_type: string | null;
  is_active: boolean;
  created_at: string;
  // Ramp 1
  ramp1_enabled: boolean;
  ramp1_target_temp: number | null;
  ramp1_pressure_bar: number | null;
  ramp1_rate_deg_min: number | null;
  ramp1_hold_minutes: number | null;
  // Ramp 2
  ramp2_enabled: boolean;
  ramp2_target_temp: number | null;
  ramp2_pressure_bar: number | null;
  ramp2_rate_deg_min: number | null;
  ramp2_hold_minutes: number | null;
  // Ramp 3
  ramp3_enabled: boolean;
  ramp3_target_temp: number | null;
  ramp3_pressure_bar: number | null;
  ramp3_rate_deg_min: number | null;
  ramp3_hold_minutes: number | null;
}

interface RecipeFormData {
  name: string;
  code: string;
  description: string;
  temperature_celsius: number;
  pressure_bar: number;
  duration_minutes: number;
  cool_down_rate: number | null;
  material_type: string;
  resin_type: string;
  is_active: boolean;
  ramp1: RampData;
  ramp2: RampData;
  ramp3: RampData;
}

const defaultRamp = (enabled: boolean = false): RampData => ({
  enabled,
  target_temp: null,
  pressure_bar: null,
  rate_deg_min: null,
  hold_minutes: null,
});

const defaultFormData: RecipeFormData = {
  name: '',
  code: '',
  description: '',
  temperature_celsius: 180,
  pressure_bar: 7,
  duration_minutes: 120,
  cool_down_rate: null,
  material_type: '',
  resin_type: '',
  is_active: true,
  ramp1: defaultRamp(true),
  ramp2: defaultRamp(false),
  ramp3: defaultRamp(false),
};

export default function CuringRecipes() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<CuringRecipe | null>(null);
  const [formData, setFormData] = useState<RecipeFormData>(defaultFormData);
  const [openRamps, setOpenRamps] = useState<Record<number, boolean>>({ 1: true, 2: false, 3: false });

  const canEdit = user?.role === 'admin' || user?.role === 'supervisor';

  // Fetch recipes
  const { data: recipes, isLoading } = useQuery({
    queryKey: ['curing-recipes'],
    queryFn: async () => {
      const { data, error } = await api
        .from('curing_recipes')
        .select('*')
        .order('name', { ascending: true });
      if (error) throw error;
      return data as CuringRecipe[];
    },
  });

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (data: RecipeFormData & { id?: string }) => {
      const payload = {
        name: data.name,
        code: data.code || null,
        description: data.description || null,
        temperature_celsius: data.ramp1.target_temp || data.temperature_celsius,
        pressure_bar: data.ramp1.pressure_bar || data.pressure_bar,
        duration_minutes: data.ramp1.hold_minutes || data.duration_minutes,
        ramp_rate: data.ramp1.rate_deg_min,
        cool_down_rate: data.cool_down_rate,
        material_type: data.material_type || null,
        resin_type: data.resin_type || null,
        is_active: data.is_active,
        updated_by: user?.id,
        // Ramp 1
        ramp1_enabled: data.ramp1.enabled,
        ramp1_target_temp: data.ramp1.target_temp,
        ramp1_pressure_bar: data.ramp1.pressure_bar,
        ramp1_rate_deg_min: data.ramp1.rate_deg_min,
        ramp1_hold_minutes: data.ramp1.hold_minutes,
        // Ramp 2
        ramp2_enabled: data.ramp2.enabled,
        ramp2_target_temp: data.ramp2.target_temp,
        ramp2_pressure_bar: data.ramp2.pressure_bar,
        ramp2_rate_deg_min: data.ramp2.rate_deg_min,
        ramp2_hold_minutes: data.ramp2.hold_minutes,
        // Ramp 3
        ramp3_enabled: data.ramp3.enabled,
        ramp3_target_temp: data.ramp3.target_temp,
        ramp3_pressure_bar: data.ramp3.pressure_bar,
        ramp3_rate_deg_min: data.ramp3.rate_deg_min,
        ramp3_hold_minutes: data.ramp3.hold_minutes,
      };

      if (data.id) {
        const { error } = await api
          .from('curing_recipes')
          .update(payload)
          .eq('id', data.id);
        if (error) throw error;
      } else {
        const { error } = await api
          .from('curing_recipes')
          .insert({ ...payload, created_by: user?.id });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['curing-recipes'] });
      toast.success(editingRecipe ? 'Receta actualizada' : 'Receta creada');
      handleCloseDialog();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Error al guardar la receta');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.from('curing_recipes').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['curing-recipes'] });
      toast.success('Receta eliminada');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Error al eliminar la receta');
    },
  });

  const handleOpenDialog = (recipe?: CuringRecipe) => {
    if (recipe) {
      setEditingRecipe(recipe);
      setFormData({
        name: recipe.name,
        code: recipe.code || '',
        description: recipe.description || '',
        temperature_celsius: recipe.temperature_celsius,
        pressure_bar: recipe.pressure_bar,
        duration_minutes: recipe.duration_minutes,
        cool_down_rate: recipe.cool_down_rate,
        material_type: recipe.material_type || '',
        resin_type: recipe.resin_type || '',
        is_active: recipe.is_active,
        ramp1: {
          enabled: recipe.ramp1_enabled ?? true,
          target_temp: recipe.ramp1_target_temp ?? recipe.temperature_celsius,
          pressure_bar: recipe.ramp1_pressure_bar ?? recipe.pressure_bar,
          rate_deg_min: recipe.ramp1_rate_deg_min ?? recipe.ramp_rate,
          hold_minutes: recipe.ramp1_hold_minutes ?? recipe.duration_minutes,
        },
        ramp2: {
          enabled: recipe.ramp2_enabled ?? false,
          target_temp: recipe.ramp2_target_temp,
          pressure_bar: recipe.ramp2_pressure_bar,
          rate_deg_min: recipe.ramp2_rate_deg_min,
          hold_minutes: recipe.ramp2_hold_minutes,
        },
        ramp3: {
          enabled: recipe.ramp3_enabled ?? false,
          target_temp: recipe.ramp3_target_temp,
          pressure_bar: recipe.ramp3_pressure_bar,
          rate_deg_min: recipe.ramp3_rate_deg_min,
          hold_minutes: recipe.ramp3_hold_minutes,
        },
      });
      setOpenRamps({
        1: true,
        2: recipe.ramp2_enabled ?? false,
        3: recipe.ramp3_enabled ?? false,
      });
    } else {
      setEditingRecipe(null);
      setFormData(defaultFormData);
      setOpenRamps({ 1: true, 2: false, 3: false });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRecipe(null);
    setFormData(defaultFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!formData.ramp1.enabled) {
      toast.error('Al menos la Rampa 1 debe estar habilitada');
      return;
    }
    saveMutation.mutate({
      ...formData,
      id: editingRecipe?.id,
    });
  };

  const handleDelete = (recipe: CuringRecipe) => {
    setDeleteDialog({ open: true, recipe });
  };

  const confirmDelete = () => {
    if (deleteDialog.recipe) {
      deleteMutation.mutate(deleteDialog.recipe.id);
    }
  };

  const updateRamp = (rampNum: 1 | 2 | 3, field: keyof RampData, value: any) => {
    const rampKey = `ramp${rampNum}` as 'ramp1' | 'ramp2' | 'ramp3';
    setFormData(prev => ({
      ...prev,
      [rampKey]: {
        ...prev[rampKey],
        [field]: value,
      },
    }));
  };

  const formatDuration = (minutes: number | null) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${mins}m`;
  };

  const countEnabledRamps = (recipe: CuringRecipe) => {
    let count = 0;
    if (recipe.ramp1_enabled) count++;
    if (recipe.ramp2_enabled) count++;
    if (recipe.ramp3_enabled) count++;
    return count;
  };

  const getTotalDuration = (recipe: CuringRecipe) => {
    let total = 0;
    if (recipe.ramp1_enabled && recipe.ramp1_hold_minutes) total += recipe.ramp1_hold_minutes;
    if (recipe.ramp2_enabled && recipe.ramp2_hold_minutes) total += recipe.ramp2_hold_minutes;
    if (recipe.ramp3_enabled && recipe.ramp3_hold_minutes) total += recipe.ramp3_hold_minutes;
    return total || recipe.duration_minutes;
  };

  const getMaxTemp = (recipe: CuringRecipe) => {
    const temps = [
      recipe.ramp1_enabled ? recipe.ramp1_target_temp : null,
      recipe.ramp2_enabled ? recipe.ramp2_target_temp : null,
      recipe.ramp3_enabled ? recipe.ramp3_target_temp : null,
    ].filter(Boolean) as number[];
    return temps.length > 0 ? Math.max(...temps) : recipe.temperature_celsius;
  };

  const RampSection = ({ rampNum, title }: { rampNum: 1 | 2 | 3; title: string }) => {
    const rampKey = `ramp${rampNum}` as 'ramp1' | 'ramp2' | 'ramp3';
    const ramp = formData[rampKey];
    const isOpen = openRamps[rampNum];
    const isPrimary = rampNum === 1;

    return (
      <Collapsible
        open={isOpen}
        onOpenChange={(open) => setOpenRamps(prev => ({ ...prev, [rampNum]: open }))}
      >
        <div className={cn(
          "rounded-lg border",
          ramp.enabled ? "border-primary/50 bg-primary/5" : "border-muted"
        )}>
          <CollapsibleTrigger className="flex w-full items-center justify-between p-4">
            <div className="flex items-center gap-3">
              {isOpen ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
              <div className="flex items-center gap-2">
                <TrendingUp className={cn(
                  "w-4 h-4",
                  ramp.enabled ? "text-primary" : "text-muted-foreground"
                )} />
                <span className="font-medium">{title}</span>
              </div>
              {ramp.enabled && ramp.target_temp && (
                <Badge variant="secondary" className="ml-2">
                  {ramp.target_temp}°C
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
              <Label htmlFor={`ramp${rampNum}-enabled`} className="text-sm text-muted-foreground">
                {ramp.enabled ? 'Habilitada' : 'Deshabilitada'}
              </Label>
              <Switch
                id={`ramp${rampNum}-enabled`}
                checked={ramp.enabled}
                onCheckedChange={(checked) => {
                  if (isPrimary && !checked) {
                    toast.error('Rampa 1 debe estar siempre habilitada');
                    return;
                  }
                  updateRamp(rampNum, 'enabled', checked);
                  if (checked) {
                    setOpenRamps(prev => ({ ...prev, [rampNum]: true }));
                  }
                }}
                disabled={isPrimary}
              />
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            {ramp.enabled && (
              <div className="p-4 pt-0 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Thermometer className="w-3 h-3" />
                      Temperatura Objetivo (°C)
                    </Label>
                    <Input
                      type="number"
                      value={ramp.target_temp || ''}
                      onChange={(e) => updateRamp(rampNum, 'target_temp', e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder="Ej: 180"
                      min={0}
                      max={500}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Gauge className="w-3 h-3" />
                      Presion (bar)
                    </Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={ramp.pressure_bar || ''}
                      onChange={(e) => updateRamp(rampNum, 'pressure_bar', e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder="Ej: 7"
                      min={0}
                      max={20}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      Velocidad de Rampa (°C/min)
                    </Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={ramp.rate_deg_min || ''}
                      onChange={(e) => updateRamp(rampNum, 'rate_deg_min', e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder="Ej: 2.5"
                      min={0}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Tiempo Mantenimiento (min)
                    </Label>
                    <Input
                      type="number"
                      value={ramp.hold_minutes || ''}
                      onChange={(e) => updateRamp(rampNum, 'hold_minutes', e.target.value ? parseInt(e.target.value) : null)}
                      placeholder="Ej: 60"
                      min={0}
                    />
                  </div>
                </div>
              </div>
            )}
          </CollapsibleContent>
        </div>
      </Collapsible>
    );
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Module Tabs */}
      <ModuleTabs
        tabs={[
          { value: 'general', label: 'Recetas Generales', href: '/recipes' },
          { value: 'curado', label: 'Recetas Curado', href: '/curing-recipes' },
        ]}
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FlaskConical className="w-6 h-6" />
            Recetas de Curado
          </h1>
          <p className="text-muted-foreground">
            Gestión de recetas con hasta 3 rampas de curado
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Nueva Receta
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : recipes?.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center p-12">
            <FlaskConical className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No hay recetas de curado</p>
            {canEdit && (
              <Button className="mt-4" onClick={() => handleOpenDialog()}>
                <Plus className="w-4 h-4 mr-2" />
                Crear primera receta
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Recetas Disponibles</CardTitle>
            <CardDescription>
              {recipes?.length} receta{recipes?.length !== 1 ? 's' : ''} configurada{recipes?.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Código</TableHead>
                  <TableHead className="text-center">Rampas</TableHead>
                  <TableHead className="text-center">
                    <Thermometer className="w-4 h-4 inline mr-1" />
                    Temp. Max
                  </TableHead>
                  <TableHead className="text-center">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Duración
                  </TableHead>
                  <TableHead>Material</TableHead>
                  <TableHead>Estado</TableHead>
                  {canEdit && <TableHead className="text-right">Acciones</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {recipes?.map((recipe) => (
                  <TableRow key={recipe.id}>
                    <TableCell className="font-medium">{recipe.name}</TableCell>
                    <TableCell>
                      {recipe.code && (
                        <Badge variant="outline">{recipe.code}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">
                        {countEnabledRamps(recipe)} rampa{countEnabledRamps(recipe) !== 1 ? 's' : ''}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">{getMaxTemp(recipe)}°C</TableCell>
                    <TableCell className="text-center">
                      {formatDuration(getTotalDuration(recipe))}
                    </TableCell>
                    <TableCell>
                      {recipe.material_type && (
                        <span className="text-sm text-muted-foreground">
                          {recipe.material_type}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={recipe.is_active ? 'default' : 'secondary'}>
                        {recipe.is_active ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(recipe)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(recipe)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Recipe Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingRecipe ? 'Editar Receta' : 'Nueva Receta de Curado'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Carbono Standard"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="code">Código</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="Ej: CARB-STD"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción de la receta"
                rows={2}
              />
            </div>

            {/* Ramps Section */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Rampas de Curado</Label>
              <p className="text-sm text-muted-foreground -mt-1">
                Configure hasta 3 rampas de temperatura. La Rampa 1 siempre está habilitada.
              </p>
              <RampSection rampNum={1} title="Rampa 1 (Principal)" />
              <RampSection rampNum={2} title="Rampa 2 (Secundaria)" />
              <RampSection rampNum={3} title="Rampa 3 (Terciaria)" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cool_down_rate">Velocidad de Enfriamiento (°C/min)</Label>
              <Input
                id="cool_down_rate"
                type="number"
                step="0.1"
                value={formData.cool_down_rate || ''}
                onChange={(e) => setFormData({ ...formData, cool_down_rate: e.target.value ? parseFloat(e.target.value) : null })}
                placeholder="Opcional"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="material_type">Tipo de Material</Label>
                <Input
                  id="material_type"
                  value={formData.material_type}
                  onChange={(e) => setFormData({ ...formData, material_type: e.target.value })}
                  placeholder="Ej: carbon_fiber"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="resin_type">Tipo de Resina</Label>
                <Input
                  id="resin_type"
                  value={formData.resin_type}
                  onChange={(e) => setFormData({ ...formData, resin_type: e.target.value })}
                  placeholder="Ej: epoxy"
                />
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label className="text-base">Activa</Label>
                <p className="text-sm text-muted-foreground">
                  La receta está disponible para usar
                </p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saveMutation.isPending}>
                {saveMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingRecipe ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <SmartConfirm
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open })}
        onConfirm={confirmDelete}
        type="delete"
        title="Eliminar Receta de Curado"
        description="Esta accion eliminara la receta permanentemente."
        affectedItems={deleteDialog.recipe ? [
          { label: "Nombre", value: deleteDialog.recipe.name },
          { label: "Temperatura max", value: deleteDialog.recipe.max_temp + "C" }
        ] : undefined}
        confirmText="Eliminar"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
